import { useState } from 'react';
import { UserPlus, User } from 'lucide-react';
import { Autocomplete } from '../Autocomplete';
import { useOrgPilots, type OrgPilot } from '../../hooks/useOrgPilots';

const ADD_NEW_ID = '__add_new__';

interface PilotComboboxProps {
  id: string;
  label: string;
  required?: boolean;
  onSelect: (pilot: OrgPilot) => void;
  initialPilot?: OrgPilot | null;
  error?: string;
}

export function PilotCombobox({
  id,
  label,
  required,
  onSelect,
  initialPilot,
  error,
}: PilotComboboxProps) {
  const { searchPilots, createPilot } = useOrgPilots();
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAirlineCode, setNewAirlineCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchResults = async (query: string): Promise<OrgPilot[]> => {
    const results = await searchPilots(query);
    const trimmed = query.trim();
    if (trimmed.length > 0) {
      const placeholder: OrgPilot = {
        id: ADD_NEW_ID,
        org_id: '',
        full_name: trimmed,
        airline_code: null,
        email: null,
        role_preference: 'any',
        linked_user_id: null,
        created_by: '',
        created_at: '',
        updated_at: '',
        licence_id: null,
        date_of_birth: null,
        nationality: null,
        base_privileges: null,
        total_flight_hours: null,
        total_pic_hours: null,
        reference_date_flthours: null,
      };
      return [...results, placeholder];
    }
    return results;
  };

  const handleSelect = (pilot: OrgPilot) => {
    if (pilot.id === ADD_NEW_ID) {
      setNewName(pilot.full_name);
      setNewAirlineCode('');
      setNewEmail('');
      setShowInlineForm(true);
      return;
    }
    setShowInlineForm(false);
    onSelect(pilot);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || saving) return;
    setSaving(true);
    try {
      const created = await createPilot({
        full_name: newName.trim(),
        airline_code: newAirlineCode.trim() || undefined,
        email: newEmail.trim() || undefined,
      });
      setShowInlineForm(false);
      onSelect(created);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-navy">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <Autocomplete<OrgPilot>
        id={id}
        fetchResults={fetchResults}
        onSelect={handleSelect}
        getLabel={(p) => p.full_name}
        getKey={(p) => p.id}
        initialItem={initialPilot ?? undefined}
        placeholder="Search pilots..."
        minChars={1}
        debounceMs={250}
        renderItem={(item, isActive) =>
          item.id === ADD_NEW_ID ? (
            <div className={`flex items-center gap-2 text-sm ${isActive ? 'text-navy' : 'text-orca-elevate'}`}>
              <UserPlus className="h-4 w-4 shrink-0" />
              <span>Add <strong>{item.full_name}</strong> as new pilot</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 shrink-0 text-steel" />
              <span className="font-semibold text-navy">{item.full_name}</span>
              {item.airline_code && (
                <span className="text-steel text-xs">({item.airline_code})</span>
              )}
            </div>
          )
        }
      />

      {error && <p className="text-red-500 text-xs mt-0.5">{error}</p>}

      {showInlineForm && (
        <div className="mt-2 p-3 border border-steel/30 rounded-md bg-white space-y-2">
          <p className="text-xs font-medium text-navy flex items-center gap-1">
            <UserPlus className="h-3.5 w-3.5" /> New Pilot
          </p>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Full name"
            className="w-full px-2 py-1.5 text-sm border border-steel/30 rounded
                       text-navy placeholder-steel/50
                       focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          />
          <div className="flex gap-2">
            <div className="w-1/2">
              <input
                type="text"
                value={newAirlineCode}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 7).toUpperCase();
                  setNewAirlineCode(cleaned);
                }}
                maxLength={7}
                placeholder="Pilot ID"
                className="w-full px-2 py-1.5 text-sm border border-steel/30 rounded
                           text-navy uppercase placeholder-steel/50
                           focus:outline-none focus:ring-2 focus:ring-orca-elevate"
              />
              <p className="text-xs text-steel/60 mt-0.5">Max 7 alphanumeric</p>
            </div>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Email (optional)"
              className="w-1/2 px-2 py-1.5 text-sm border border-steel/30 rounded
                         text-navy placeholder-steel/50
                         focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowInlineForm(false)}
              className="px-3 py-1 text-xs text-steel hover:text-navy transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || !newName.trim()}
              onClick={handleCreate}
              className="px-3 py-1 text-xs font-medium text-white bg-navy rounded
                         hover:bg-navy/90 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Adding...' : 'Add Pilot'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
